/**
 * HLF Enterprise Pincode Delivery Checker
 * Custom HTML Element: <hlf-pincode-checker>
 *
 * Bug fixes applied:
 * - BUG-1:  Fixed undefined `state` variable in GPS detect flow
 * - BUG-2:  Auto-geolocation now uses IP-only (no browser permission popup on load)
 * - BUG-3:  Fixed showLoading / showSuccessState ordering (race / flicker)
 * - BUG-4:  pdc_geo_checked cleared on "Change" click
 * - BUG-5:  Loading spinner delayed until after geolocation permission granted
 * - BUG-6:  API timeout race condition guarded with handled flag
 * - BUG-8:  Null safety for getRepresentativePostOffice return value
 * - BUG-9:  Timer text uses querySelector instead of fragile nextSibling
 * - BUG-10: Improved region mapping accuracy using two-digit pincode prefix
 * - BUG-11: Debounce on detect location button via _isDetecting flag
 * - BUG-12: All localStorage calls wrapped in try/catch helpers
 */

class HlfPincodeChecker extends HTMLElement {
  constructor() {
    super();

    // DOM Elements
    this.container = this;
    this.form = this.querySelector('.hlf-pincode-form');
    this.input = this.querySelector('.hlf-pincode-input');
    this.submitBtn = this.querySelector('.hlf-pincode-submit-btn');
    this.detectBtn = this.querySelector('.hlf-pincode-detect-btn');
    this.clearBtn = this.querySelector('.hlf-pincode-clear-btn');
    this.errorMsg = this.querySelector('.hlf-pincode-error-msg');
    this.successBox = this.querySelector('.hlf-pincode-success-box');
    this.successLocation = this.querySelector('.hlf-pincode-location-text');
    this.successDate = this.querySelector('.hlf-pincode-date-text');
    this.changeBtn = this.querySelector('.hlf-pincode-change-btn');
    this.timerText = this.querySelector('.hlf-pincode-urgency-timer');
    this.urgencyBox = this.querySelector('.hlf-pincode-urgency');

    // Configurations
    this.cutoffHour = parseInt(this.dataset.cutoffHour || '17', 10); // Default 5 PM

    // BUG-11: Debounce flag for detect location
    this._isDetecting = false;

    // Bind Methods
    this.handleCheck = this.handleCheck.bind(this);
    this.handleDetectLocation = this.handleDetectLocation.bind(this);
    this.handleClear = this.handleClear.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.cleanInput = this.cleanInput.bind(this);
    this.updateUrgencyTimer = this.updateUrgencyTimer.bind(this);
  }

  connectedCallback() {
    // Event Listeners
    if (this.input) {
      this.input.addEventListener('input', this.cleanInput);
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          this.handleCheck();
        }
      });
    }

    if (this.submitBtn) this.submitBtn.addEventListener('click', this.handleCheck);
    if (this.detectBtn) this.detectBtn.addEventListener('click', this.handleDetectLocation);
    if (this.clearBtn) this.clearBtn.addEventListener('click', this.handleClear);
    if (this.changeBtn) this.changeBtn.addEventListener('click', this.handleChange);

    // Initial State Check
    this.initializeState();

    // Start Urgency Countdown Timer (every second)
    this.updateUrgencyTimer();
    this.timerInterval = setInterval(this.updateUrgencyTimer, 1000);
  }

  disconnectedCallback() {
    if (this.timerInterval) clearInterval(this.timerInterval);
  }

  // ── BUG-12: Safe localStorage helpers ─────────────────────────────────
  _getStorage(key) {
    try { return localStorage.getItem(key); } catch (e) { return null; }
  }

  _setStorage(key, value) {
    try { localStorage.setItem(key, value); } catch (e) { /* silent – Safari private browsing */ }
  }

  _removeStorage(key) {
    try { localStorage.removeItem(key); } catch (e) { /* silent */ }
  }

  // ── Initialisation ────────────────────────────────────────────────────
  initializeState() {
    const savedPincode = this._getStorage('pdc_saved_pincode');
    const savedLocation = this._getStorage('pdc_saved_location');
    const savedDateText = this._getStorage('pdc_saved_date_text');

    if (savedPincode && savedLocation && savedDateText) {
      this.showSuccessState(savedPincode, savedLocation, savedDateText);
    } else {
      // BUG-2 FIX: runAutoGeolocation now uses IP-only detection
      // (no intrusive browser permission popup on page load)
      const geoChecked = this._getStorage('pdc_geo_checked') === 'true';
      if (!geoChecked) {
        this.runAutoGeolocation();
      }
    }
    this.toggleClearButton();
  }

  // ── Input helpers ─────────────────────────────────────────────────────
  cleanInput() {
    // Allow only numeric digits, max 6 characters
    this.input.value = this.input.value.replace(/\D/g, '').substring(0, 6);
    this.toggleClearButton();
    this.clearError();
  }

  toggleClearButton() {
    if (!this.clearBtn) return;
    if (this.input && this.input.value.length > 0) {
      this.clearBtn.style.display = 'flex';
    } else {
      this.clearBtn.style.display = 'none';
    }
  }

  // ── Error / Loading states ────────────────────────────────────────────
  clearError() {
    this.container.classList.remove('is-invalid');
    if (this.errorMsg) {
      this.errorMsg.style.display = 'none';
      this.errorMsg.innerHTML = '';
    }
  }

  showError(message) {
    this.container.classList.add('is-invalid');
    if (this.errorMsg) {
      this.errorMsg.style.display = 'flex';
      this.errorMsg.innerHTML = `
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <span>${message}</span>
      `;
    }
    // Remove shake class after animation completes so it can shake again
    setTimeout(() => {
      this.container.classList.remove('is-invalid');
    }, 400);
  }

  showLoading(loading = true) {
    if (loading) {
      this.container.classList.add('is-loading');
      if (this.input) this.input.disabled = true;
      if (this.submitBtn) {
        this.submitBtn.disabled = true;
        this.submitBtn.dataset.originalText = this.submitBtn.textContent;
        this.submitBtn.innerHTML = '<span class="hlf-btn-spinner"></span>';
      }
      if (this.detectBtn) {
        this.detectBtn.classList.add('is-detecting');
        this.detectBtn.disabled = true;
      }
    } else {
      this.container.classList.remove('is-loading');
      this._isDetecting = false; // BUG-11: Reset detect flag when loading ends
      if (this.input) this.input.disabled = false;
      if (this.submitBtn) {
        this.submitBtn.disabled = false;
        this.submitBtn.textContent = this.submitBtn.dataset.originalText || 'Check';
      }
      if (this.detectBtn) {
        this.detectBtn.classList.remove('is-detecting');
        this.detectBtn.disabled = false;
      }
    }
  }

  // ── User actions ──────────────────────────────────────────────────────
  handleClear(e) {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
      e.stopPropagation();
    }
    if (this.input) {
      this.input.value = '';
      this.input.focus();
    }
    this.toggleClearButton();
    this.clearError();
  }

  handleChange(e) {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
      e.stopPropagation();
    }
    // Clear all saved details
    this._removeStorage('pdc_saved_pincode');
    this._removeStorage('pdc_saved_location');
    this._removeStorage('pdc_saved_date_text');
    this._removeStorage('pdc_geo_checked'); // BUG-4 FIX: Allow re-detection after change

    // Update layout state
    this.container.classList.remove('has-saved-pincode');
    if (this.successBox) this.successBox.style.display = 'none';
    // Remove delivery notice banner
    const notice = this.container.querySelector('.hlf-delivery-notice');
    if (notice) notice.remove();
    if (this.form) this.form.style.display = 'block';
    if (this.input) {
      this.input.value = '';
      this.input.focus();
    }
    this.toggleClearButton();
    this.clearError();
  }

  // ── Manual pincode check ──────────────────────────────────────────────
  handleCheck(e) {
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
      e.stopPropagation();
    }
    const pincode = this.input.value.trim();
    if (!/^[1-9][0-9]{5}$/.test(pincode)) {
      this.showError('Please enter a valid 6-digit Indian pincode.');
      return;
    }

    this.clearError();
    this.showLoading(true);

    // BUG-6 FIX: Guard flag to prevent race between timeout and late API response
    let handled = false;

    const apiTimeout = setTimeout(() => {
      if (handled) return;
      handled = true;
      console.warn('Postal API timed out, falling back to local database.');
      this.performLocalFallbackLookup(pincode);
    }, 4500);

    // Hit Public Postal API
    fetch(`https://api.postalpincode.in/pincode/${pincode}`)
      .then(response => response.json())
      .then(data => {
        clearTimeout(apiTimeout);
        if (handled) return; // BUG-6: Timeout already handled this request
        handled = true;
        this.showLoading(false);

        if (data && data[0] && data[0].Status === 'Success' && data[0].PostOffice && data[0].PostOffice.length > 0) {
          const representativeOffice = this.getRepresentativePostOffice(data[0].PostOffice);
          const locationText = this.formatDeliveryLocation(representativeOffice);
          // BUG-8 FIX: Optional chaining prevents crash when representativeOffice is null
          const deliveryRange = this.calculateDeliveryRangeByState(representativeOffice?.State || '');
          const dateText = this.getFormattedDeliveryDateRange(deliveryRange.min, deliveryRange.max);

          // Save in localStorage
          this._setStorage('pdc_saved_pincode', pincode);
          this._setStorage('pdc_saved_location', locationText);
          this._setStorage('pdc_saved_date_text', dateText);

          this.showSuccessState(pincode, locationText, dateText);
        } else {
          this.showError('Pincode not serviceable or invalid. Try another.');
        }
      })
      .catch(err => {
        clearTimeout(apiTimeout);
        if (handled) return; // BUG-6: Already handled by timeout
        handled = true;
        console.error('Postal API Error:', err);
        this.performLocalFallbackLookup(pincode);
      });
  }

  // ── Local fallback lookup ─────────────────────────────────────────────
  performLocalFallbackLookup(pincode) {
    this.showLoading(false);

    // BUG-10 FIX: Use two-digit prefix for more accurate region mapping
    const firstDigit = pincode.charAt(0);
    const firstTwoNum = parseInt(pincode.substring(0, 2), 10);
    let regionState = 'India';
    let stateName = '';

    switch (firstDigit) {
      case '1':
        // 11=Delhi, 12-13=Haryana, 14-16=Punjab, 17=HP, 18-19=J&K/Chandigarh
        if (firstTwoNum === 11) {
          regionState = 'Delhi'; stateName = 'Delhi';
        } else if (firstTwoNum >= 12 && firstTwoNum <= 13) {
          regionState = 'Haryana'; stateName = 'Haryana';
        } else if (firstTwoNum >= 14 && firstTwoNum <= 16) {
          regionState = 'Punjab'; stateName = 'Punjab';
        } else if (firstTwoNum === 17) {
          regionState = 'Himachal Pradesh'; stateName = 'Himachal Pradesh';
        } else {
          regionState = 'Jammu & Kashmir'; stateName = 'Jammu & Kashmir';
        }
        break;
      case '2':
        if (firstTwoNum >= 20 && firstTwoNum <= 28) {
          regionState = 'Uttar Pradesh'; stateName = 'Uttar Pradesh';
        } else {
          regionState = 'Uttarakhand'; stateName = 'Uttarakhand';
        }
        break;
      case '3':
        if (firstTwoNum >= 30 && firstTwoNum <= 34) {
          regionState = 'Rajasthan'; stateName = 'Rajasthan';
        } else {
          regionState = 'Gujarat'; stateName = 'Gujarat';
        }
        break;
      case '4':
        if (firstTwoNum >= 40 && firstTwoNum <= 44) {
          regionState = 'Maharashtra'; stateName = 'Maharashtra';
        } else {
          regionState = 'Madhya Pradesh'; stateName = 'Madhya Pradesh';
        }
        break;
      case '5':
        if (firstTwoNum >= 50 && firstTwoNum <= 53) {
          regionState = 'Telangana & Andhra Pradesh'; stateName = 'Telangana';
        } else {
          regionState = 'Karnataka'; stateName = 'Karnataka';
        }
        break;
      case '6':
        if (firstTwoNum >= 60 && firstTwoNum <= 64) {
          regionState = 'Tamil Nadu'; stateName = 'Tamil Nadu';
        } else {
          regionState = 'Kerala'; stateName = 'Kerala';
        }
        break;
      case '7':
        if (firstTwoNum >= 70 && firstTwoNum <= 74) {
          regionState = 'West Bengal'; stateName = 'West Bengal';
        } else {
          regionState = 'North-East India'; stateName = 'Assam';
        }
        break;
      case '8':
        if (firstTwoNum >= 80 && firstTwoNum <= 85) {
          regionState = 'Bihar & Jharkhand'; stateName = 'Bihar';
        } else {
          regionState = 'Odisha'; stateName = 'Odisha';
        }
        break;
      case '9':
        regionState = 'Army Post Office'; stateName = '';
        break;
      default:
        regionState = 'Standard Delivery'; stateName = '';
    }

    const locationText = regionState;
    const deliveryRange = this.calculateDeliveryRangeByState(stateName);
    const dateText = this.getFormattedDeliveryDateRange(deliveryRange.min, deliveryRange.max);

    this._setStorage('pdc_saved_pincode', pincode);
    this._setStorage('pdc_saved_location', locationText);
    this._setStorage('pdc_saved_date_text', dateText);

    this.showSuccessState(pincode, locationText, dateText);
  }

  // ── Success state UI ──────────────────────────────────────────────────
  showSuccessState(pincode, location, dateText) {
    this.container.classList.add('has-saved-pincode');

    if (this.successLocation) {
      this.successLocation.textContent = `${location} (${pincode})`;
    }
    if (this.successDate) {
      this.successDate.innerHTML = `Est. delivery: <strong>${dateText}</strong>`;
    }

    // Delivery Notice Banner (e.g. monsoon, festive rush)
    this.renderDeliveryNotice();

    if (this.form) this.form.style.display = 'none';
    if (this.successBox) this.successBox.style.display = 'block';
  }

  renderDeliveryNotice() {
    // Remove existing notice if any (prevents duplicates)
    const existing = this.container.querySelector('.hlf-delivery-notice');
    if (existing) existing.remove();

    const isEnabled = this.dataset.deliveryNoticeEnabled === 'true';
    const message = this.dataset.deliveryNoticeMessage;

    if (!isEnabled || !message) return;

    const notice = document.createElement('div');
    notice.className = 'hlf-delivery-notice';
    notice.innerHTML = `
      <svg class="hlf-delivery-notice-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">
        <path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/>
        <path d="M8 19v2m4-2v2m4-2v2"/>
      </svg>
      <span class="hlf-delivery-notice-text">${message}</span>
    `;

    // Insert as the last row inside the success card
    if (this.successBox) {
      this.successBox.appendChild(notice);
    } else {
      this.container.appendChild(notice);
    }
  }

  // ── Post Office helpers ───────────────────────────────────────────────
  getRepresentativePostOffice(postOffices = []) {
    if (!Array.isArray(postOffices) || postOffices.length === 0) {
      return null;
    }

    // Priority 1: Sub Office (usually more specific town/locality)
    const subOffice = postOffices.find((office) =>
      office.BranchType && office.BranchType.toLowerCase().includes("sub")
    );
    if (subOffice) return subOffice;

    // Priority 2: Head Office (fallback if no Sub Office exists)
    const headOffice = postOffices.find((office) =>
      office.BranchType && office.BranchType.toLowerCase().includes("head")
    );
    if (headOffice) return headOffice;

    // Priority 3: Branch Office
    const branchOffice = postOffices.find((office) =>
      office.BranchType && office.BranchType.toLowerCase().includes("branch")
    );
    if (branchOffice) return branchOffice;

    // Priority 4: First available record
    return postOffices[0];
  }

  formatDeliveryLocation(postOffice) {
    if (!postOffice) {
      return "";
    }

    const locationName =
      postOffice.District ||
      postOffice.Name ||
      postOffice.Block ||
      "your area";

    const state = postOffice.State || "";

    return state ? `${locationName}, ${state}` : locationName;
  }

  // ── Delivery estimate calculation ─────────────────────────────────────
  calculateDeliveryRangeByState(state) {
    if (!state) {
      return { min: 5, max: 8 };
    }

    const cleanState = state.toLowerCase().trim();

    // Warehouse state / very close region (Punjab / Chandigarh / Haryana / Delhi)
    const zoneSameOrClosest = [
      'punjab',
      'chandigarh',
      'haryana',
      'delhi'
    ];

    // Nearby north states from Punjab
    const zoneNearbyNorth = [
      'rajasthan',
      'himachal pradesh',
      'uttar pradesh',
      'uttarakhand'
    ];

    // Medium distance from Punjab
    const zoneMedium = [
      'bihar',
      'jharkhand',
      'madhya pradesh',
      'chhattisgarh',
      'gujarat'
    ];

    // Longer distance but common courier coverage
    const zoneLong = [
      'maharashtra',
      'karnataka',
      'tamil nadu',
      'telangana',
      'west bengal',
      'andhra pradesh',
      'odisha',
      'kerala',
      'goa'
    ];

    // Remote / difficult logistics
    const zoneRemote = [
      'assam',
      'meghalaya',
      'manipur',
      'mizoram',
      'tripura',
      'nagaland',
      'arunachal pradesh',
      'sikkim',
      'jammu & kashmir',
      'jammu and kashmir',
      'ladakh',
      'andaman & nicobar islands',
      'lakshadweep'
    ];

    if (zoneSameOrClosest.some(s => cleanState.includes(s))) {
      return { min: 1, max: 3 };
    }

    if (zoneNearbyNorth.some(s => cleanState.includes(s))) {
      return { min: 2, max: 4 };
    }

    if (zoneMedium.some(s => cleanState.includes(s))) {
      return { min: 4, max: 6 };
    }

    if (zoneLong.some(s => cleanState.includes(s))) {
      return { min: 5, max: 7 };
    }

    if (zoneRemote.some(s => cleanState.includes(s))) {
      return { min: 7, max: 10 };
    }

    return { min: 5, max: 8 };
  }

  // ── Date formatting helpers ───────────────────────────────────────────
  getFormattedDeliveryDateRange(minDays, maxDays) {
    const startDate = this.getFormattedDeliveryDate(minDays);
    const endDate = this.getFormattedDeliveryDate(maxDays);
    return `${startDate} - ${endDate}`;
  }

  getFormattedDeliveryDate(daysToAdd) {
    const date = new Date();
    // Add delivery days, skipping Sundays (courier holiday)
    let added = 0;
    while (added < daysToAdd) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0) { // Sunday is 0
        added++;
      }
    }

    // Formatting: e.g. "Tue, 9 Jun"
    const days = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${days[date.getDay()]}, ${date.getDate()} ${months[date.getMonth()]}`;
  }

  // ── Address Formatting ────────────────────────────────────────────────
  buildDetailedLocation(address, isIPFallback = false) {
    if (!address) return '';
    const parts = [];
    
    if (!isIPFallback) {
      // Map specific structured fields for a highly accurate local address
      if (address.road) parts.push(address.road);
      if (address.neighbourhood) parts.push(address.neighbourhood);
      if (address.suburb) parts.push(address.suburb);
      if (address.city_district) parts.push(address.city_district);
    }
    
    // Add primary locality
    const city = address.city || address.town || address.village || address.county;
    if (city) parts.push(city);
    
    // Always include state
    if (address.state) parts.push(address.state);

    // Remove duplicates (e.g. city and city_district might share the same name)
    const uniqueParts = [];
    parts.forEach(part => {
      if (part && !uniqueParts.some(u => u.toLowerCase() === part.toLowerCase())) {
        uniqueParts.push(part);
      }
    });

    return uniqueParts.join(', ');
  }

  // ── Geolocation System Integration ────────────────────────────────────
  // BUG-2 FIX: Auto-geolocation uses IP-only detection (no browser permission popup)
  // GPS detection is reserved for explicit user clicks on the detect button
  runAutoGeolocation() {
    this._setStorage('pdc_geo_checked', 'true');
    this.fallbackToIPGeolocation(true); // silent = true, IP-only
  }

  handleDetectLocation(e) {
    let silent = false;
    if (e && typeof e.preventDefault === 'function') {
      e.preventDefault();
      e.stopPropagation();
    } else if (e === true) {
      silent = true;
    }

    // BUG-11 FIX: Prevent double-clicks / concurrent detect requests
    if (this._isDetecting) return;
    this._isDetecting = true;

    this.clearError();

    // BUG-5 FIX: Only show detect button animation initially, not full loading spinner.
    // Full loading is shown after geolocation permission is granted.
    if (this.detectBtn) {
      this.detectBtn.classList.add('is-detecting');
      this.detectBtn.disabled = true;
    }

    if (!navigator.geolocation) {
      this.showLoading(true);
      if (!silent) this.showError('Geolocation is not supported by your browser.');
      this.fallbackToIPGeolocation(silent);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        // Permission granted — now show full loading for the API call
        this.showLoading(true);
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;

        // Tier 1: Reverse Geocoding with OpenStreetMap Nominatim API
        fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&accept-language=en&zoom=18&addressdetails=1`)
          .then(res => res.json())
          .then(data => {
            const postcode = data && data.address && (data.address.postcode || data.address.zip);
            const sanitizedPincode = postcode ? postcode.replace(/\D/g, '').substring(0, 6) : '';

            if (sanitizedPincode && /^[1-9][0-9]{5}$/.test(sanitizedPincode)) {
              let fullLocationText = this.buildDetailedLocation(data.address, false);

              if (!fullLocationText && data.display_name) {
                const rawParts = data.display_name.split(',');
                const cleanParts = rawParts
                  .map(part => part.trim())
                  .filter(part => {
                    if (!part) return false;
                    if (part.toLowerCase() === 'india') return false;
                    if (/^[1-9][0-9]{5}$/.test(part)) return false;
                    return true;
                  });

                const uniqueParts = [];
                cleanParts.forEach(part => {
                  if (!uniqueParts.includes(part)) {
                    uniqueParts.push(part);
                  }
                });

                fullLocationText = uniqueParts.join(', ');
              }

              if (!fullLocationText) {
                const address = data.address || {};
                const city = address.city || address.town || address.village || address.suburb || 'your area';
                const addrState = address.state || '';
                fullLocationText = addrState ? `${city}, ${addrState}` : city;
              }

              // BUG-1 FIX: Use data.address.state, NOT undefined 'state' variable
              const detectedState = data?.address?.state || '';
              const deliveryRange = this.calculateDeliveryRangeByState(detectedState);
              const dateText = this.getFormattedDeliveryDateRange(deliveryRange.min, deliveryRange.max);

              // Save in localStorage
              this._setStorage('pdc_saved_pincode', sanitizedPincode);
              this._setStorage('pdc_saved_location', fullLocationText);
              this._setStorage('pdc_saved_date_text', dateText);

              if (this.input) this.input.value = sanitizedPincode;
              this.toggleClearButton();
              // BUG-3 FIX: showLoading(false) BEFORE showSuccessState to prevent UI flicker
              this.showLoading(false);
              this.showSuccessState(sanitizedPincode, fullLocationText, dateText);
            } else {
              console.warn('GPS address detected but postcode missing:', data);

              const gpsAddress = data && data.address ? data.address : {};
              const fullLocationText = this.buildDetailedLocation(gpsAddress, false);

              this.showLoading(false);

              if (fullLocationText) {
                if (!silent) {
                  this.showError('Location detected, but pincode was not found. Please enter pincode manually.');
                }
                return;
              }

              this.fallbackToIPGeolocation(silent);
            }
          })
          .catch(err => {
            console.error('Coordinates lookup error:', err);
            this.fallbackToIPGeolocation(silent);
          });
      },
      (error) => {
        // User denied geolocation or error occurred
        console.warn('Coordinates permission denied or failed, running Tier 2 IP detection.');
        this.showLoading(true);
        this.fallbackToIPGeolocation(silent);
      },
      { timeout: 8000 }
    );
  }

  fallbackToIPGeolocation(silent = false) {
    // Tier 2: IP-Based Geolocation Fallback (GeoJS API)
    fetch('https://get.geojs.io/v1/ip/geo.json')
      .then(res => res.json())
      .then(geo => {
        const lat = geo && geo.latitude;
        const lon = geo && geo.longitude;

        if (lat && lon) {
          // Reverse geocode the IP coordinates using Nominatim
          fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}&accept-language=en`)
            .then(res => res.json())
            .then(data => {
              this.showLoading(false);
              const postcode = data && data.address && (data.address.postcode || data.address.zip);
              const sanitizedPincode = postcode ? postcode.replace(/\D/g, '').substring(0, 6) : '';

              if (sanitizedPincode && /^[1-9][0-9]{5}$/.test(sanitizedPincode)) {
                let fullLocationText = this.buildDetailedLocation(data.address, true);

                if (!fullLocationText && data.display_name) {
                  const rawParts = data.display_name.split(',');
                  const cleanParts = rawParts
                    .map(part => part.trim())
                    .filter(part => {
                      if (!part) return false;
                      if (part.toLowerCase() === 'india') return false;
                      if (/^[1-9][0-9]{5}$/.test(part)) return false;
                      return true;
                    });

                  const uniqueParts = [];
                  cleanParts.forEach(part => {
                    if (!uniqueParts.includes(part)) {
                      uniqueParts.push(part);
                    }
                  });

                  fullLocationText = uniqueParts.join(', ');
                }

                if (!fullLocationText) {
                  const address = data.address || {};
                  const city = address.city || address.town || address.village || address.suburb || 'your area';
                  const state = address.state || '';
                  fullLocationText = state ? `${city}, ${state}` : city;
                }

                const deliveryRange = this.calculateDeliveryRangeByState(data.address ? data.address.state : '');
                const dateText = this.getFormattedDeliveryDateRange(deliveryRange.min, deliveryRange.max);

                // Save in localStorage
                this._setStorage('pdc_saved_pincode', sanitizedPincode);
                this._setStorage('pdc_saved_location', fullLocationText);
                this._setStorage('pdc_saved_date_text', dateText);

                if (this.input) this.input.value = sanitizedPincode;
                this.toggleClearButton();
                this.showSuccessState(sanitizedPincode, fullLocationText, dateText);
              } else {
                if (!silent) this.showError('Could not resolve location. Please enter pincode manually.');
              }
            })
            .catch(err => {
              this.showLoading(false);
              console.error('IP coordinates reverse lookup failed:', err);
              if (!silent) this.showError('Could not resolve location. Please enter pincode manually.');
            });
        } else {
          this.showLoading(false);
          if (!silent) this.showError('Could not resolve location. Please enter pincode manually.');
        }
      })
      .catch(err => {
        this.showLoading(false);
        console.error('IP Geolocation lookup failed:', err);
        if (!silent) this.showError('Could not resolve location. Please enter pincode manually.');
      });
  }

  // ── Same-Day Urgency Countdown Logic ──────────────────────────────────
  updateUrgencyTimer() {
    if (!this.timerText) return;

    const now = new Date();
    const cutoff = new Date();
    cutoff.setHours(this.cutoffHour, 0, 0, 0);

    let isToday = true;
    let diffMs = cutoff.getTime() - now.getTime();

    if (diffMs < 0) {
      // Cutoff has passed today, target tomorrow's cutoff
      cutoff.setDate(cutoff.getDate() + 1);
      diffMs = cutoff.getTime() - now.getTime();
      isToday = false;
    }

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);

    // Format timer digits: HHh MMm SSs
    const formattedTime = `${hours.toString().padStart(2, '0')}h ${minutes.toString().padStart(2, '0')}m ${seconds.toString().padStart(2, '0')}s`;

    this.timerText.textContent = formattedTime;

    // BUG-9 FIX: Use querySelector instead of fragile nextSibling (whitespace text nodes)
    // Update all urgency message text elements (may exist in multiple places)
    const msgTextEls = this.querySelectorAll('.hlf-pincode-urgency-msg-text');
    msgTextEls.forEach(msgTextEl => {
      const shipDay = isToday ? 'today' : 'tomorrow';
      const expectedText = ` to ship ${shipDay}`;
      if (msgTextEl.textContent !== expectedText) {
        msgTextEl.textContent = expectedText;
      }
    });
  }
}

// Define Custom Element
if (!customElements.get('hlf-pincode-checker')) {
  customElements.define('hlf-pincode-checker', HlfPincodeChecker);
}
